import { useEffect, useMemo, useState } from 'react';

import { UserCircleIcon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { CandidateCheckScreens } from 'constants/candidate_screens';
import { buildNavPath } from 'helpers/candidate_application/form-page-helpers';
import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import { useQueryParams } from 'hooks/query-params';
import i18n from 'i18n';
import moment from 'moment';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Select } from 'components/FormComponents';
import { FormMessage } from 'components/FormComponents/FormMessage/FormMessage';
import { IconComponent } from 'components/Icon/IconComponent';
import SliderMotion from 'components/Motion/SliderMotion';
import { ProgressBar } from 'components/ProgressBar/ProgressBar';

import {
  getCandidateApplication,
  reprocessStaticContent,
  selectCandidateApplication,
} from 'api/candidate-application-slice';

import { Loading } from '../../components/Loading/Loading';
import { CandidateFooter } from './CandidateFooter/CandidateFooter';
import styles from './CandidatePage.module.scss';

const CandidatePage = ({
  loading,
  token,
  fullScreen,
  footer = false,
  children,
  withTopLogo,
  displayLanguageSelector = false,
  brand,
  allowAfterSubmission = false, // Allow rendering children even after candidate submitted (for document requests)
  replacement,
}) => {
  useDocumentTitle();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [admin] = useQueryParams(['admin']);

  const {
    application: { candidate, check_values, candidate_checks },
    fetched,
  } = useSelector(selectCandidateApplication);

  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    window.onpopstate = () => {
      navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`, admin));
    };
  }, [admin, navigate, token]);

  useEffect(() => {
    if (!candidate?.id || process.env.REACT_APP_ENV !== 'prod') {
      return;
    }

    window.Intercom('shutdown');

    window.Intercom('boot', {
      app_id: 'yh3o8vqn',
      name: candidate.name,
      email: candidate.email,
      user_id: `candidate-${candidate.id}-${process.env.REACT_APP_ENV}`,
      user_type: 'candidate',
      created_at: moment(candidate.created_at, 'DD/MM/YYYY').unix(),
    });
  }, [candidate]);

  useEffect(() => {
    if (fetched) setFetching(false);
    else
      dispatch(getCandidateApplication(token)).then(({ payload }) => {
        if (payload?.result?.candidate_checks?.length < 1) {
          navigate(
            buildNavPath(
              `/form_submission/candidate/${CandidateCheckScreens.SUCCESS}/${token}`,
              admin,
            ),
          );
        }

        setFetching(false);
      });
  }, []);

  // Listen for language changes and reprocess static content
  useEffect(() => {
    const handleLanguageChange = () => {
      dispatch(reprocessStaticContent());
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [dispatch]);

  const allChecksSubmitted = useMemo(() => {
    return candidate_checks?.length > 0 && candidate_checks?.every(check => check.submitted_at);
  }, [candidate_checks]);

  return (
    <div className={styles.root}>
      <div>
        {withTopLogo && (
          <img
            className={styles.root_logo}
            src={
              brand?.brand_active && brand?.brand_logo
                ? brand.brand_logo
                : '/assets/images/checkmate/icon-label.svg'
            }
          />
        )}
      </div>
      {allChecksSubmitted &&
      !check_values.rejected &&
      !admin &&
      !allowAfterSubmission &&
      !replacement ? (
        <SliderMotion key="success-application" duration={0.6} delay={0.15}>
          <div
            className={cn(
              'd-flex flex-column u-gap-large align-items-center u-justify-self-center u-padding u-width-75',
              styles['candidate-submitted-application'],
            )}>
            <img
              src="assets/images/success-application.jpg"
              alt="Application submitted illustration"
              className=" u-border-radius--half u-width-75"
            />
            <FormMessage
              type="Info"
              closeable={false}
              message={t('candidate.dashboard.candidateFormCompleted')}
            />
          </div>
        </SliderMotion>
      ) : (
        <>
          {displayLanguageSelector && (
            <div className={styles.root_actionTopRight}>
              <Select
                name="candidate_locale"
                value={i18n.language === 'en' ? 'en_GB' : i18n.language}
                useDefault
                onChange={e => {
                  i18n.changeLanguage(e.target.value);
                }}>
                <Select.Item value="en_GB">English (UK)</Select.Item>
                <Select.Item value="en_US">English (USA)</Select.Item>
              </Select>
            </div>
          )}
          <Loading active={fetching || loading}></Loading>
          <div className={cn(styles.content, { [styles.contentFullScreen]: fullScreen })}>
            {children}
          </div>
          {footer && <CandidateFooter />}
        </>
      )}
    </div>
  );
};

const Header = ({
  brand,
  candidateName,
  subtitle,
  showLogo = true,
  showUserDetails = false,
  userDetails = null,
}) => (
  <AnimatePresence mode="wait">
    <SliderMotion key={candidateName} duration={0.6} isBackward={true}>
      <header
        className={cn(
          styles.header,
          'd-flex',
          'flex-column',
          'justify-content-center',
          'align-items-center',
          'u-margin-bottom--large',
        )}>
        {showLogo && (
          <img
            className={styles.logo}
            src={
              brand?.brand_active && brand?.brand_logo
                ? brand.brand_logo
                : '/assets/images/checkmate/icon-label.svg'
            }
          />
        )}
        <div
          className={cn(
            styles.headerWrapper,
            'd-flex',
            'flex-column',
            'justify-content-center',
            'u-margin-top--large',
            'u-width-100',
            'u-gap-large',
          )}>
          {!showUserDetails ? (
            <div className="d-flex flex-column align-items-start u-gap-small">
              <IconComponent icon={UserCircleIcon} className={styles.icon} size="xlarge" />
              <h1 className="primary-text u-font-size-xxx-large u-font-weight-500">
                Hi {candidateName}
              </h1>
            </div>
          ) : (
            <div className="d-flex justify-content-between flex-wrap align-items-start u-width-100 u-gap-large">
              <div className="d-flex flex-column u-gap-small">
                <div className="d-flex align-items-center u-gap">
                  <IconComponent icon={UserCircleIcon} className={styles.icon} size="large" />
                  <div className="d-flex flex-column">
                    <span className="primary-text u-font-size-large u-font-weight-500">
                      {userDetails?.name}
                    </span>
                    <span className="secondary-text u-font-size-x-small">{userDetails?.email}</span>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column align-items-center u-gap-small">
                <ProgressBar
                  current={userDetails?.completedSections}
                  total={userDetails?.totalSections}
                  label="Sections Completed"
                  showLabel={true}
                  showPercentage={true}
                  size="medium"
                  variant="success"
                  animated={true}
                />
              </div>
            </div>
          )}
          {subtitle && (
            <span className="secondary-text u-font-size-small u-font-weight-400">{subtitle}</span>
          )}
        </div>
      </header>
    </SliderMotion>
  </AnimatePresence>
);

const Logo = ({ path }) => <img className={styles.logo} src={path} />;

const View = ({ children, className }) => (
  <div className={cn(styles.view, { [className]: className })}>{children}</div>
);

const Form = ({ children, className }) => (
  <form className={cn(styles.form, { [className]: className })}>{children}</form>
);

const Card = ({ children, className }) => (
  <div
    className={cn(styles.card, 'card', 'u-padding--large', {
      [className]: className,
    })}>
    {children}
  </div>
);

CandidatePage.Logo = Logo;
CandidatePage.View = View;
CandidatePage.Form = Form;
CandidatePage.Card = Card;
CandidatePage.Header = Header;

export default CandidatePage;
